import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { WarehouseAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { Capability, FeatureKey } from '../rbac/features';

interface PermissionState {
  permissions: Record<string, Capability>;
  roleName: string | null;
  role: string | null;
  isAdmin: boolean;
  loading: boolean;
  refresh: () => void;
}

const EMPTY_PERMISSIONS: Record<string, Capability> = {};

const PermissionContext = createContext<PermissionState | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

// Mounted INSIDE AuthProvider (needs the token/user) and OUTSIDE the router (so route
// guards can read it). Fetches /api/auth/me/permissions on mount and whenever the user
// changes. Fails CLOSED on a transient error (empty permissions -> everything hidden) with
// a non-blocking retry, and NEVER logs the user out — a 401 is handled by the global axios
// interceptor; a transient network/5xx error is swallowed here.
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<Record<string, Capability>>(EMPTY_PERMISSIONS);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    // Auth is still hydrating `user` from localStorage. A null `user` here means
    // "unknown", NOT "logged out" — taking the logged-out branch now would flip
    // loading=false during the auth/permission race and let PermissionRoute evaluate
    // can()/Navigate before the real permissions are known (bouncing a deep-route
    // refresh to '/'). Keep permissions as "still loading" until auth settles; the
    // effect re-runs when authLoading flips false (it is in the dependency array).
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user) {
      setPermissions(EMPTY_PERMISSIONS);
      setRoleName(null);
      setRole(null);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    WarehouseAPI.getMyPermissions()
      .then((data) => {
        if (cancelled) return;
        setPermissions(data.permissions || EMPTY_PERMISSIONS);
        setRoleName(data.roleName);
        setRole(data.role);
        setIsAdmin(!!data.isAdmin);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail closed: empty permissions (everything hidden), non-blocking retry, no logout.
        setPermissions(EMPTY_PERMISSIONS);
        setIsAdmin(false);
        setLoading(false);
        retryTimer = setTimeout(() => {
          if (!cancelled) setReloadKey((k) => k + 1);
        }, 15000);
      });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, reloadKey]);

  const value: PermissionState = {
    permissions,
    roleName,
    role,
    isAdmin,
    loading,
    refresh,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissionContext = (): PermissionState => {
  const ctx = useContext(PermissionContext);
  if (ctx === undefined) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return ctx;
};

export type { FeatureKey };
